import React, { useState, useRef, useMemo, useEffect } from 'react';
import debounce from 'lodash/debounce';

export default function RangeSlider({ min, max, stateMin, stateMax, onMinChange, onMaxChange, disableLeftThumb }) {
    const [values, setValues] = useState([stateMin, stateMax]);
    const sliderRef = useRef(null);
    const dragging = useRef(null);
    const step = 1;
    const debounceMs = 15;
    const debouncedOnMinChange = useMemo(() => debounce((val) => onMinChange(val), debounceMs), [onMinChange]);
    const debouncedOnMaxChange = useMemo(() => debounce((val) => onMaxChange(val), debounceMs), [onMaxChange]);

    useEffect(() => {
        if (!dragging.current) {
            setValues([stateMin, stateMax]);
        }
    }, [stateMin, stateMax]);

    const valueToPercent = (value) => ((value - min) / (max - min)) * 100;

    const positionToValue = (posX) => {
        const slider = sliderRef.current;
        if (!slider) return min;
        const { left, width } = slider.getBoundingClientRect();
        let percent = (posX - left) / width;
        percent = Math.min(Math.max(0, percent), 1);
        let val = min + percent * (max - min);
        val = Math.round(val / step) * step;
        return Math.min(Math.max(min, val), max);
    };

    const updateValue = (thumb, newValue) => {
        setValues((old) => {
            let [minVal, maxVal] = old;
            if (thumb === 'min') {
                minVal = Math.min(newValue, maxVal - step);
                minVal = Math.max(min, minVal);
                debouncedOnMinChange(minVal);
            } else {
                maxVal = Math.max(newValue, minVal + step);
                maxVal = Math.min(max, maxVal);
                debouncedOnMaxChange(maxVal);
            }
            return [minVal, maxVal];
        });
    };

    const onTouchMove = (e) => {
        if (!dragging.current) return;
        e.preventDefault();
        const touch = e.touches[0];
        const newValue = positionToValue(touch.clientX);
        updateValue(dragging.current, newValue);
    };

    const onTouchStart = (thumb) => (e) => {
        dragging.current = thumb;
        e.currentTarget.addEventListener('touchmove', onTouchMove);
    };

    const onTouchEnd = (e) => {
        dragging.current = null;
        e.currentTarget.removeEventListener('touchmove', onTouchMove);
        window.removeEventListener('touchend', onTouchEnd);
    };

    const onMouseMove = (e) => {
        if (!dragging.current) return;
        e.preventDefault();
        const newValue = positionToValue(e.clientX);
        updateValue(dragging.current, newValue);
    };

    const onMouseDown = (thumb) => (e) => {
        e.preventDefault();
        dragging.current = thumb;
        e.currentTarget.addEventListener('mousemove', onMouseMove, { passive: false });
    };

    const onMouseUp = (e) => {
        dragging.current = null;
        e.currentTarget.removeEventListener('mousemove', onMouseMove, { passive: false });
        window.removeEventListener('mouseup', onMouseUp);
    };

    const onTrackPointerDown = (e) => {
        let clientX;
        if (e.touches) {
            clientX = e.touches[0].clientX;
        } else {
            clientX = e.clientX;
        }
        const clickedValue = positionToValue(clientX);
        const [minVal, maxVal] = values;
        const distToMin = Math.abs(clickedValue - minVal);
        const distToMax = Math.abs(clickedValue - maxVal);
        const closestThumb = !disableLeftThumb && distToMin <= distToMax ? 'min' : 'max';
        updateValue(closestThumb, clickedValue);
        dragging.current = closestThumb;
        if (e.touches) {
            window.addEventListener('touchmove', onTouchMove, { passive: false });
            window.addEventListener('touchend', onTouchEnd);
        } else {
            window.addEventListener('mousemove', onMouseMove, { passive: false });
            window.addEventListener('mouseup', onMouseUp);
        }
    };
    const minPercent = valueToPercent(values[0]);
    const maxPercent = valueToPercent(values[1]);

    return (
        <div
            className="range-slider"
            role="slider"
            aria-valuenow={values[0]}
            tabIndex={0}
            ref={sliderRef}
            onMouseDown={onTrackPointerDown}
            onTouchStart={onTrackPointerDown}
            aria-label="Select price range"
        >
            <div className="range-slider__line range-slider__line--out-of-range" />
            <div
                className="range-slider__line range-slider__line--in-range"
                style={{
                    left: `${minPercent}%`,
                    width: `${Math.min(100 - minPercent, maxPercent - minPercent)}%`,
                }}
            />
            {!disableLeftThumb && (
                <div
                    className="range-slider__thumb"
                    role="slider"
                    tabIndex={0}
                    aria-valuemin={min}
                    aria-valuemax={values[1] - step}
                    aria-valuenow={values[0]}
                    onMouseDown={onMouseDown('min')}
                    onMouseUp={onMouseUp}
                    onTouchStart={onTouchStart('min')}
                    onTouchEnd={onTouchEnd}
                    aria-label="Select minimum price"
                    style={{ left: `${Math.max(0, minPercent)}%` }}
                />
            )}
            <div
                className="range-slider__thumb"
                role="slider"
                tabIndex={0}
                aria-valuemin={values[0] + step}
                aria-valuemax={max}
                aria-valuenow={values[1]}
                onMouseDown={onMouseDown('max')}
                onMouseUp={onMouseUp}
                onTouchStart={onTouchStart('max')}
                onTouchEnd={onTouchEnd}
                aria-label="Select maximum price"
                style={{ left: `${Math.min(100, maxPercent)}%` }}
            />
        </div>
    );
}
